import useConnectionAuthFields from 'new/hooks/adapters/useConnectionAuthFields';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'new/elements/Form/useForm';
import { FORM_TYPE_EDIT_CREDENTIAL } from 'js/constants/formTypes';
import { formatValidationErrorMessage, getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import useForms from 'new/state/forms/repository/useForms';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import UIUtils from 'js/lib/UIUtils';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import useCredentialsAdapter from 'new/state/credentials/adapter/useCredentialsAdapter';
import useProbe from 'new/state/probe/repository/useProbe';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { tryCatchAsync } from 'new/utils/TryCatch';

export type CredentialSaveResult = {
  success: boolean;
  credentialName?: string;
};

const unnamedCredential = {
  name: 'cred_name',
  display_name: 'Credential Name',
} as const;

export default function useCredentialsPage({ useFormData, formType }) {
  const { setLoaderMessages, setValidation, setContinueBlocked } = useForm();
  const {
    selectedConnection,
    credentialId,
    selectedCredentialInfo,
    addedCredentialId,
    updateFormData,
    resetFormData,
    connectionType,
    data,
  } = useFormData();
  const { clearAllProbe } = useProbe();
  const { data: editCredentialData, setError: setEditError } = useForms(FORM_TYPE_EDIT_CREDENTIAL);
  const { setError } = useForms(formType);
  const { nextPage } = useForm();
  const [selectedTemplate, setSelectedTemplate] = useState(selectedCredentialInfo?.template);

  const { fields } = useConnectionAuthFields({
    connectionType: selectedConnection,
    selectedTemplate,
  });

  const credential = editCredentialData?.credentialData;

  const { showSuccess } = useSnackbar();
  const { updateCredential } = useCredentialsAdapter();
  const { verifyCredential, verifyCredentialSilent, addCredential } = useFormsAdapter(formType);

  const skipAutoRedirectionValue = useRef(false);

  /**
   * Build initial data to render the necessary fields
   */
  useEffect(() => {
    updateFormData({
      selectedCredentialInfo: UIUtils.configWithMissingDefaults({}, fields),
    });
  }, []);

  const validateCredential = useCallback(async () => {
    if (!credentialId) return false; // no credential selected, validation fails

    const eventName =
      connectionType === 'SINK'
        ? 'data_sink_credential_selection'
        : 'data_source_credential_selection';
    AnalyticsUtils.captureCustomEvent(eventName, {
      credential_id: credentialId,
      connection_type: selectedConnection,
    });

    try {
      const response: any = await verifyCredential(credentialId);
      return response?.payload?.formData?.credentialProbeStatus?.isValid;
    } catch (err: any) {
      // verifyCredentialFromForm resolves to verifyCredentialSilent under the hood,
      // so the rejection never hit the global ErrorDisplay. Surface it on the form
      // slice so the user sees a banner when Continue is pressed against a bad cred.
      setError(err?.message || 'Unable to authenticate.');
      return false;
    }
  }, [credentialId, verifyCredential, connectionType, selectedConnection, setError]);

  useEffect(() => {
    setValidation(validateCredential);
  }, [validateCredential, credentialId]); // re-register validation when credentialId changes

  useDidUpdateEffect(() => {
    if (!credentialId) return;
    if (skipAutoRedirectionValue.current) return;

    // Picking a login kicks off two checks in the background: first it
    // confirms the login works, then it loads the full details. The Continue
    // button needs to stay disabled through both — turning it back on too
    // early would let someone click through before everything finished
    // loading (NEX-21503).
    let isStale = false;
    setContinueBlocked?.(true);

    (async () => {
      try {
        const promise = verifyCredentialSilent(credentialId);
        const { error } = await tryCatchAsync(promise);

        // If the person switches to a different login while these checks
        // are still running, we mark this run as no longer relevant. An
        // outdated run must not do anything once it finally finishes — no
        // error message, and no moving to the next step — otherwise it
        // could interfere with whatever the newer login selection is doing.
        if (isStale) return;

        if (error) {
          setError(error.message || 'Unable to authenticate.');
          return;
        }

        await nextPage(undefined, true);
      } finally {
        if (!isStale) setContinueBlocked?.(false);
      }
    })();

    return () => {
      isStale = true;
    };
  }, [credentialId]);

  const onSaveCredential = async (): Promise<CredentialSaveResult> => {
    const missingFields = getEmptyRequiredFields(fields, selectedCredentialInfo);

    skipAutoRedirectionValue.current = true;

    if (!selectedCredentialInfo?.cred_name) missingFields.unshift(unnamedCredential);

    if (selectedCredentialInfo?.template) {
      selectedCredentialInfo.auth_template_id = Number(selectedCredentialInfo?.template);
    }

    const errorMessage = formatValidationErrorMessage(missingFields);

    if (errorMessage) {
      setError(errorMessage);
      return { success: false };
    }

    const response = await addCredential(selectedConnection, selectedCredentialInfo);

    const createdCredentialId = response?.result;

    if (!createdCredentialId) {
      setError('Not able to create credential');
      return { success: false };
    }

    const createdCredential = response?.entities?.credentials[createdCredentialId];
    const credentialName = createdCredential?.name || 'Credential';

    // Set credentialId but not addedCredentialId yet — avoids triggering nextPage() before probe
    updateFormData({
      selectedCredentialInfo: null,
      credentialId: createdCredentialId,
    });

    const { error: probeError } = await tryCatchAsync(verifyCredentialSilent(createdCredentialId));

    if (probeError) {
      // Don't set addedCredentialId — that would fire nextPage() → validateCredential
      // → a second non-silent verifyCredential, racing with setError and dispatching
      // the global ErrorDisplay. Keep the user on this page with one clear banner.
      setError(probeError?.message || 'Connection test failed.');
      return { success: false };
    }

    showSuccess(`${credentialName} created successfully`);
    updateFormData({ addedCredentialId: createdCredential });

    return { success: true, credentialName };
  };

  const onChangeTemplate = (template) => {
    setSelectedTemplate(template);
  };

  useDidUpdateEffect(() => {
    if (addedCredentialId) {
      nextPage();
    }
  }, [addedCredentialId]);

  const onCredentialSelected = (
    credential: { id: number },
    skipNextNavigationParam: boolean = false,
  ) => {
    skipAutoRedirectionValue.current = skipNextNavigationParam;

    const isEditMode = !!data?.sink?.id;

    const fieldsToReset = [
      'selectedCredential',
      'selectedBucket',
      'selectedTable',
      'table',
      'isNewTable',
      'credentialId',
      'discoveryResults',
      'selectedFeeds',
      'feedFields',
      'feedFieldsLoading',
      'expandedFeeds',
      'vendorFeedSpecs',
      'availableFeeds',
      'templateVersionInfo',
    ];

    if (!isEditMode) {
      resetFormData(fieldsToReset);
    }

    clearAllProbe();
    updateFormData({
      credentialId: credential.id,
      selectedCredential: credential,
    });
  };

  const onCredentialInfoUpdated = (newInfo, field, value) => {
    updateFormData({
      selectedCredentialInfo: UIUtils.fillConditionalDefault(newInfo, fields, field, value),
    });
  };

  const onCredentialInfoBatchUpdate = (selectedCredentialInfo) =>
    updateFormData({ selectedCredentialInfo });

  const onEditCredential = async (): Promise<{
    success: boolean;
    saveError?: boolean;
    probeError?: string;
  }> => {
    if (!credential) {
      setEditError('Not able to edit credential. Missing info.');
      return { success: false, saveError: true };
    }

    const missingFields = getEmptyRequiredFields(fields, credential);
    const errorMessage = formatValidationErrorMessage(missingFields, {
      credentialNameError: !credential.cred_name ? 'Please enter a name for this credential' : null,
    });

    if (errorMessage) {
      setEditError(errorMessage);
      return { success: false, saveError: true };
    }

    const finalCredentialInfo = UIUtils.configWithMissingDefaults(credential, fields);

    if (finalCredentialInfo?.template) {
      finalCredentialInfo.auth_template_id = Number(finalCredentialInfo?.template);
    }

    try {
      await updateCredential(credentialId, finalCredentialInfo, FORM_TYPE_EDIT_CREDENTIAL);
      // Save + probe both succeeded — advance to next step (matches new-credential flow).
      await nextPage(undefined, true);
      return { success: true };
    } catch (err: any) {
      if (err?.isSaveError) {
        // Save failed — show error inside the edit credential form (modal stays open)
        setEditError(err.message || 'Unable to save credential.');
        return { success: false, saveError: true };
      }
      // Save succeeded, probe failed — return for caller to display as warning
      return { success: false, probeError: err?.message || 'Connection test failed.' };
    }
  };

  return {
    fields,
    selectedTemplate,
    onSaveCredential,
    onChangeTemplate,
    onEditCredential,
    onCredentialInfoUpdated,
    onCredentialInfoBatchUpdate,
    onCredentialSelected,
  };
}
